import React, { useState, useEffect, useContext, useCallback } from "react";
import GlobalContext from "contexts/GlobalContext";
import Page from "pages/Page";
import {
  Content,
  Link,
  Pagination,
  AddAlt24,
  Form,
  TextInput,
  ValueAppProjectList,
  Modal,
  useNavigator,
  paginator,
  isGoogleBot,
  usePathFactory,
  useTranslator,
  PageMedia,
  DocumentDownload24,
} from "@sia/style-guide";
import i18n from "i18next";
import KeycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import { Link as RouterLink } from "react-router-dom";
import "./ValueApp.scss";

const ValueApp = ({ isSubscribedToValueApp }) => {
  const translate = useTranslator();
  const navigate = useNavigator();
  const { language, user } = useContext(GlobalContext);
  const pathFactory = usePathFactory();
  const [page, setPage] = useState(1);
  const [projectPreviewsItems, setProjectPreviewsItems] = useState([]);
  const [paginatedItems, setPaginatedItems] = useState([]);
  const [perPageLimit, setPerPageLimit] = useState(10);
  const userAgent = navigator.userAgent;
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [projectTitle, setProjectTitle] = useState("");
  const TERMS_URL = process.env.REACT_APP_VALUE_APP_TERMS_URL;
  const CMS_URL = process.env.REACT_APP_CMS_URL;
  const PDF_URL = process.env.REACT_APP_VALUEAPP_PDF;
  const IMG_URL = process.env.REACT_APP_VALUEAPP_IMAGE;
  const refreshProjects = useCallback(() => {
    pgAdapter.getValueAppProjectList(
      (response) => {
        const { data } = response;
        // Map API data to the preview items that include per-project actions.
        const items = data.map((project) => ({
          id: project.id,
          title: project.title,
          status: project.status.name,
          project_id: project.project_id,
          user_id: project.user_id,
          url: `manage/widget/${project.project_id}`,
          translate: translate,
          actions: {
            change: () => navigate(`value-app/manage/widget/${project.project_id}`),
            remove: () => {
              pgAdapter.closeValueAppProject(
                { project_id: project.project_id },
                (response) => {
                  refreshProjects();
                },
                (error) => {
                  console.error("Error closing project", error);
                }
              );
            },
          },
        }));
        setProjectPreviewsItems(items);
      },
      (error) => {
        console.error("Error retrieving project list:", error);
      }
    );
  }, [navigate, translate]);

  useEffect(() => {
    if (user && isSubscribedToValueApp) {
      refreshProjects();
    }
  }, [user, isSubscribedToValueApp, refreshProjects]);

  const onClickSubscribe = () => {
    navigate("account/services");
  };

  const onPaginate = (targetPage, perPageLimit) => {
    setPage(targetPage);
    setPerPageLimit(perPageLimit);
    setPaginatedItems(paginator.paginateItems(projectPreviewsItems, targetPage, perPageLimit));
  };

  useEffect(() => {
    setPage(1);
    if (isGoogleBot(userAgent)) {
      setPaginatedItems(paginator.paginateItems(projectPreviewsItems, 1, projectPreviewsItems.length));
    } else {
      setPaginatedItems(paginator.paginateItems(projectPreviewsItems, 1, perPageLimit));
    }
  }, [projectPreviewsItems, perPageLimit, userAgent]);

  const renderLoginLink = () => {
    const afterLoginRedirectUrl = window.location.origin + pathFactory.create("value-app");
    return (
      <Link
        withLeadIcon
        /**
         * Using callback target instead of string, otherwise it causes:
         * - error in keycloak-js, probably lifecycle/timing problem
         * - infinite reload
         */
        target={() => (window.location.href = KeycloakIDPAdapter.createLoginUrl(i18n.language, afterLoginRedirectUrl))}
      >
        {translate("value-app.login")}
      </Link>
    );
  };

  const handleSaveProject = () => {
    const projectData = {
      title: projectTitle,
    };

    pgAdapter.addValueAppProject(
      projectData,
      (response) => {
        refreshProjects();
      },

      (error) => {
        console.error("Error adding project:", error);
      }
    );
    setProjectTitle("");
    setShowProjectForm(false);
  };

  const renderProjectFormButton = (
    <>
      <button
        className="ValueAppProject-Addbtn"
        onClick={() => {
          setShowDisclaimerModal(showProjectForm ? false : true);
          setShowProjectForm(false);
        }}
      >
        <AddAlt24 />
        <span className={"add-button"}>{translate("value-app.project.add")}</span>
      </button>
      {!showProjectForm && user && isSubscribedToValueApp && !(projectPreviewsItems.length > 0) && (
        <div className="value-app-main-container">
          <p className={"sia-text-06"}> {translate("value-app.no-projects")}</p>
        </div>
      )}
      {(showDisclaimerModal || showProjectForm) && (
        <Content width={"full"}>
          {showDisclaimerModal && (
            <Modal
              title={translate("value-app.disclaimer.heading")}
              lead={
                <>
                  <p className={"sia-text-10"}>{translate("value-app.disclaimer.message1")}</p>
                  <p className={"sia-text-10"}>{translate("value-app.disclaimer.message2")}</p>
                </>
              }
              confirmLabel={translate("modal.button.confirm")}
              cancelLabel={translate("modal.button.cancel")}
              onConfirm={() => {
                setShowDisclaimerModal(false);
                setShowProjectForm(true);
              }}
              onCancel={() => {
                setShowDisclaimerModal(false);
              }}
            />
          )}
          {showProjectForm && (
            <div className="ValueAppProjectForm">
              <Form primaryButtonLabel={translate("value-app.project.save")} onClickPrimaryButton={handleSaveProject}>
                <TextInput
                  id="projectTitle"
                  label={translate("value-app.project.title")}
                  required
                  value={projectTitle}
                  onChange={(event, value) => setProjectTitle(value)}
                />
              </Form>
            </div>
          )}
        </Content>
      )}
    </>
  );

  // const img = `${CMS_URL}/${language}/${IMG_URL}?resolution=desktop`;
  const image = [
    {
      src: `https://cms.sia.ch/${language}/api/getMedia/1427?resolution=desktop`,
      type: "IMAGE",
      copyright: {
        title: translate("value-app.image-caption"),
        copyright: "SIA",
      },
    },
  ];
  const pdf = `https://cms.sia.ch/${language}/api/getMedia/1445`;
  // const pdf = `${CMS_URL}/${language}/${PDF_URL}`;

  return (
    language && (
      <Page title={translate("header.app-switcher.value-app.name")} className="ValueApp-page">
        <PageMedia items={image} customConfig={{ dots: false }} showCounter={false} />
        <div className="value-app-main-container sia-text-10">
          <h1>{translate("value-app.title")}</h1>
          <p>{translate("value-app.paragraph-1")} </p>
          <Link className="ValueApp-pdf" target={pdf}>
            <DocumentDownload24 />
            <span className={"add-button"}>{translate("value-app.pdf")}</span>
          </Link>
          <p>
            <p>
              <strong> {translate("value-app.paragraph-2")} </strong>
            </p>
            <Link target={process.env.REACT_APP_VALUEAPP_FORM_URL} withLeadIcon>
              {translate("value-app.feedback.form")}
            </Link>
          </p>

          <p className={"sia-margin-05-top"}>
            <span>
              <strong> {translate("value-app.paragraph-3")} </strong>
            </span>
            <p>{translate("value-app.paragraph-4")}</p>
          </p>
          {user
            ? !isSubscribedToValueApp && (
                <>
                  <p>{translate("value-app.no-access")}</p>
                  <Link target={onClickSubscribe} withLeadIcon>
                    {translate("value-app.subscribe")}
                  </Link>
                </>
              )
            : renderLoginLink()}
          <>
            <Link target={`/${language}/${TERMS_URL}`} withLeadIcon>
              {translate("value-app.terms")}
            </Link>

            {user && isSubscribedToValueApp && renderProjectFormButton}
          </>
        </div>

        {user && isSubscribedToValueApp && projectPreviewsItems.length > 0 && (
          <>
            <ValueAppProjectList items={paginatedItems} elementToRender={RouterLink} />
            {projectPreviewsItems.length > 10 && (
              <Content width={"full"}>
                {isGoogleBot(userAgent) ? null : <Pagination items={projectPreviewsItems} onChange={onPaginate} translate={translate} page={page} />}
              </Content>
            )}
          </>
        )}
      </Page>
    )
  );
};

export default ValueApp;
